import {
  Timestamp,
  type DocumentData,
} from 'firebase/firestore';

import {
  Package,
  type PersistedPackageProps,
} from '@domain/entities/Package';
import { Money } from '@domain/value-objects/Money';
import {
  DayType,
  Season,
  SectionMode,
  type DayTypeValue,
  type SeasonValue,
  type SectionModeValue,
} from '@domain/value-objects/PackageEnums';
import {
  assertIncludedServicesForWrite,
  type IncludedServices,
  type MenIncludedServices,
  type WomenIncludedServices,
} from '@domain/value-objects/IncludedServices';
import type { PackageDocument } from '@/infrastructure/firebase/documents/PackageDocument';

/**
 * يحوّل كيان الباقة إلى مستند Firestore قياسي كامل.
 *
 * الكتابة الكاملة مقصودة حتى تُحذف أي أسماء حقول قديمة عند تعديل الباقة.
 */
export function packageToDocument(pkg: Package): PackageDocument {
  const p = pkg.props;

  if (p.includedServices) {
    assertIncludedServicesForWrite(
      p.includedServices,
      p.sectionMode === SectionMode.MEN ? 'men' : 'women'
    );
  }
  if (p.includedServicesMen) {
    assertIncludedServicesForWrite(p.includedServicesMen, 'men');
  }
  if (p.includedServicesWomen) {
    assertIncludedServicesForWrite(p.includedServicesWomen, 'women');
  }

  return {
    key: p.key,
    name: p.name,
    price: p.price.toSAR(),
    capacityMin: p.capacityMin,
    capacityMax: p.capacityMax,
    ...(p.sectionMode === SectionMode.BOTH
      ? {
          capacityMenMin: p.capacityMenMin!,
          capacityMenMax: p.capacityMenMax!,
          capacityWomenMin: p.capacityWomenMin!,
          capacityWomenMax: p.capacityWomenMax!,
        }
      : {}),
    sectionMode: p.sectionMode,
    dayType: p.dayType,
    season: p.season,
    currency: 'SAR',
    hasIncludedServices: p.hasIncludedServices,
    ...(p.includedServicesText
      ? { includedServicesText: p.includedServicesText }
      : {}),
    ...(p.includedServices
      ? { includedServices: p.includedServices }
      : {}),
    ...(p.includedServicesMenText
      ? { includedServicesMenText: p.includedServicesMenText }
      : {}),
    ...(p.includedServicesMen
      ? { includedServicesMen: p.includedServicesMen }
      : {}),
    ...(p.includedServicesWomenText
      ? { includedServicesWomenText: p.includedServicesWomenText }
      : {}),
    ...(p.includedServicesWomen
      ? { includedServicesWomen: p.includedServicesWomen }
      : {}),
    ...(p.notes ? { notes: p.notes } : {}),
    venueId: p.venueId,
    createdAt: Timestamp.fromDate(p.createdAt),
    updatedAt: Timestamp.fromDate(p.updatedAt),
  };
}

/**
 * يقرأ مستند باقة قياسياً. الاستثناء المؤقت الوحيد أثناء طرح حقل الاسم هو
 * fallback إلى "اسم الباقة" للمستندات القديمة؛ جميع الكتابات الجديدة تحفظ name.
 */
export function packageFromDocument(
  packageId: string,
  venueId: string,
  data: DocumentData
): Package {
  const storedVenueId = optionalString(data['venueId'], 'venueId');
  if (storedVenueId !== undefined && storedVenueId !== venueId) {
    throw invalidField(
      packageId,
      'venueId',
      `مطابقاً لمعرّف القاعة "${venueId}"`
    );
  }

  const hasIncludedServices = requireBoolean(
    data['hasIncludedServices'],
    packageId,
    'hasIncludedServices'
  );
  const includedServices = optionalIncludedServices(
    data['includedServices'],
    packageId,
    'includedServices'
  );
  const includedServicesMen = optionalIncludedServices(
    data['includedServicesMen'],
    packageId,
    'includedServicesMen'
  ) as MenIncludedServices | undefined;
  const includedServicesWomen = optionalIncludedServices(
    data['includedServicesWomen'],
    packageId,
    'includedServicesWomen'
  ) as WomenIncludedServices | undefined;
  const includedServicesMenText = optionalString(
    data['includedServicesMenText'],
    'includedServicesMenText'
  );
  const includedServicesWomenText = optionalString(
    data['includedServicesWomenText'],
    'includedServicesWomenText'
  );

  if (
    !hasIncludedServices &&
    (includedServices !== undefined ||
      includedServicesMen !== undefined ||
      includedServicesWomen !== undefined)
  ) {
    throw invalidField(
      packageId,
      'includedServices',
      'غير موجود عندما تكون hasIncludedServices=false'
    );
  }

  const sectionMode = requireSectionMode(
    data['sectionMode'],
    packageId
  );
  const capacityMin = requirePositiveInteger(
    data['capacityMin'],
    packageId,
    'capacityMin'
  );
  const capacityMax = requirePositiveInteger(
    data['capacityMax'],
    packageId,
    'capacityMax'
  );
  if (capacityMin > capacityMax) {
    throw invalidField(packageId, 'capacityMin', 'أقل من أو يساوي capacityMax');
  }
  const sectionRanges = readSectionRanges(
    data,
    packageId,
    sectionMode,
    capacityMin,
    capacityMax
  );
  const includedServicesText = optionalString(
    data['includedServicesText'],
    'includedServicesText'
  );
  const notes = optionalString(data['notes'], 'notes');

  const props: PersistedPackageProps = {
    key: requireString(data['key'], packageId, 'key'),
    venueId,
    // Rollout-safe read for packages created before the `name` migration.
    // All new/edited writes require and persist a real package name.
    name: optionalString(data['name'], 'name') ?? 'اسم الباقة',
    price: Money.fromSAR(
      requireNonNegativeInteger(data['price'], packageId, 'price')
    ),
    capacityMin,
    capacityMax,
    ...sectionRanges,
    sectionMode,
    dayType: requireDayType(data['dayType'], packageId),
    season: requireSeason(data['season'], packageId),
    currency: requireCurrency(data['currency'], packageId),
    hasIncludedServices,
    ...(includedServicesText ? { includedServicesText } : {}),
    ...(includedServices ? { includedServices } : {}),
    ...(includedServicesMenText ? { includedServicesMenText } : {}),
    ...(includedServicesMen ? { includedServicesMen } : {}),
    ...(includedServicesWomenText ? { includedServicesWomenText } : {}),
    ...(includedServicesWomen ? { includedServicesWomen } : {}),
    ...(notes ? { notes } : {}),
    createdAt: requireTimestamp(
      data['createdAt'],
      packageId,
      'createdAt'
    ).toDate(),
    updatedAt: requireTimestamp(
      data['updatedAt'],
      packageId,
      'updatedAt'
    ).toDate(),
  };

  return Package.fromPersistence(packageId, props);
}

function readSectionRanges(
  data: DocumentData,
  packageId: string,
  sectionMode: SectionModeValue,
  capacityMin: number,
  capacityMax: number
): Pick<
  PersistedPackageProps,
  | 'capacityMenMin'
  | 'capacityMenMax'
  | 'capacityWomenMin'
  | 'capacityWomenMax'
> {
  const fields = [
    'capacityMenMin',
    'capacityMenMax',
    'capacityWomenMin',
    'capacityWomenMax',
  ] as const;
  if (sectionMode !== SectionMode.BOTH) {
    const unexpected = fields.find((field) => data[field] !== undefined);
    if (unexpected) {
      throw invalidField(packageId, unexpected, 'غير مخزن لباقات القسم الواحد');
    }
    return {};
  }

  const capacityMenMin = requirePositiveInteger(
    data['capacityMenMin'], packageId, 'capacityMenMin'
  );
  const capacityMenMax = requirePositiveInteger(
    data['capacityMenMax'], packageId, 'capacityMenMax'
  );
  const capacityWomenMin = requirePositiveInteger(
    data['capacityWomenMin'], packageId, 'capacityWomenMin'
  );
  const capacityWomenMax = requirePositiveInteger(
    data['capacityWomenMax'], packageId, 'capacityWomenMax'
  );
  if (capacityMenMin > capacityMenMax || capacityWomenMin > capacityWomenMax) {
    throw invalidField(packageId, 'capacityMin', 'نطاقات أقسام صالحة');
  }
  if (
    capacityMin !== capacityMenMin + capacityWomenMin ||
    capacityMax !== capacityMenMax + capacityWomenMax
  ) {
    throw invalidField(packageId, 'capacityMin', 'مجموع نطاقي الرجال والنساء');
  }
  return {
    capacityMenMin,
    capacityMenMax,
    capacityWomenMin,
    capacityWomenMax,
  };
}

function requireString(
  value: unknown,
  packageId: string,
  field: string
): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw invalidField(packageId, field, 'نصاً غير فارغ');
  }
  return value.trim();
}

function optionalString(
  value: unknown,
  field: string
): string | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'string') {
    throw new Error(`حقل الباقة الاختياري "${field}" يجب أن يكون نصاً`);
  }
  const normalized = value.trim();
  return normalized || undefined;
}

function requireBoolean(
  value: unknown,
  packageId: string,
  field: string
): boolean {
  if (typeof value !== 'boolean') {
    throw invalidField(packageId, field, 'قيمة منطقية');
  }
  return value;
}

function requireNonNegativeInteger(
  value: unknown,
  packageId: string,
  field: string
): number {
  if (
    typeof value !== 'number' ||
    !Number.isInteger(value) ||
    value < 0
  ) {
    throw invalidField(packageId, field, 'عدداً صحيحاً غير سالب');
  }
  return value;
}

function requirePositiveInteger(
  value: unknown,
  packageId: string,
  field: string
): number {
  if (
    typeof value !== 'number' ||
    !Number.isInteger(value) ||
    value < 1
  ) {
    throw invalidField(packageId, field, 'عدداً صحيحاً موجباً');
  }
  return value;
}

function requireTimestamp(
  value: unknown,
  packageId: string,
  field: string
): Timestamp {
  if (!(value instanceof Timestamp)) {
    throw invalidField(packageId, field, 'Firestore Timestamp');
  }
  return value;
}

function requireCurrency(
  value: unknown,
  packageId: string
): 'SAR' {
  if (value !== 'SAR') {
    throw invalidField(packageId, 'currency', 'SAR');
  }
  return value;
}

function requireSectionMode(
  value: unknown,
  packageId: string
): SectionModeValue {
  if (
    value !== SectionMode.MEN &&
    value !== SectionMode.WOMEN &&
    value !== SectionMode.BOTH
  ) {
    throw invalidField(packageId, 'sectionMode', 'نوع قسم معتمداً');
  }
  return value;
}

function requireDayType(
  value: unknown,
  packageId: string
): DayTypeValue {
  if (
    value !== DayType.ALL &&
    value !== DayType.WEEKDAY &&
    value !== DayType.WEEKEND
  ) {
    throw invalidField(packageId, 'dayType', 'نوع يوم معتمداً');
  }
  return value;
}

function requireSeason(
  value: unknown,
  packageId: string
): SeasonValue {
  if (
    value !== Season.ALL &&
    value !== Season.SCHOOL &&
    value !== Season.SUMMER &&
    value !== Season.HOLIDAY &&
    value !== Season.EID
  ) {
    throw invalidField(packageId, 'season', 'موسماً معتمداً');
  }
  return value;
}

function optionalIncludedServices(
  value: unknown,
  packageId: string,
  field: string
): IncludedServices | undefined {
  if (value === undefined) return undefined;
  if (
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value)
  ) {
    throw invalidField(packageId, field, 'خريطة بيانات');
  }

  const includedServices = value as IncludedServices;
  try {
    const audience = field === 'includedServicesMen'
      ? 'men'
      : field === 'includedServicesWomen'
        ? 'women'
        : undefined;
    assertIncludedServicesForWrite(includedServices, audience);
  } catch (error) {
    throw invalidField(
      packageId,
      field,
      (error as Error).message
    );
  }
  return includedServices;
}

function invalidField(
  packageId: string,
  field: string,
  expected: string
): Error {
  return new Error(
    `تعذّر قراءة الباقة "${packageId}": الحقل "${field}" يجب أن يكون ${expected}`
  );
}
